import { tz } from '@date-fns/tz'
import { addDays, addMonths, addWeeks, addYears, format } from 'date-fns'

export type RecurrenceFrequency = 'day' | 'week' | 'month' | 'year'

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export function computeLastOccurrence(
  startDate: Date,
  frequency: RecurrenceFrequency,
  interval: number,
  amount: number,
  timeZone: string,
): Date {
  const steps = interval * (amount - 1)
  const options = { in: tz(timeZone) }

  switch (frequency) {
    case 'day':
      return addDays(startDate, steps, options)
    case 'week':
      return addWeeks(startDate, steps, options)
    case 'month':
      return addMonths(startDate, steps, options)
    case 'year':
      return addYears(startDate, steps, options)
  }
}

export function buildSummary(
  startDate: Date,
  frequency: RecurrenceFrequency,
  interval: number,
  amount: number,
  timeZone: string,
  iterationsLeft?: number,
): string {
  const tzOption = { in: tz(timeZone) }
  const dayOfWeek = DAYS_OF_WEEK[startDate.getDay()]
  const timeStr = format(startDate, 'h:mm a', tzOption)

  let cadenceStr: string
  if (interval === 1) {
    cadenceStr =
      frequency === 'day'
        ? 'daily'
        : frequency === 'week'
          ? `every ${dayOfWeek}`
          : frequency === 'month'
            ? `on the ${format(startDate, 'do', tzOption)} of every month`
            : `every year on ${format(startDate, 'MMM do', tzOption)}`
  } else {
    cadenceStr =
      frequency === 'day'
        ? `every ${interval} days`
        : frequency === 'week'
          ? `every ${interval} weeks on ${dayOfWeek}`
          : frequency === 'month'
            ? `on the ${format(startDate, 'do', tzOption)} every ${interval} months`
            : `every ${interval} years on ${format(startDate, 'MMM do', tzOption)}`
  }

  const lastDate = computeLastOccurrence(
    startDate,
    frequency,
    interval,
    amount,
    timeZone,
  )
  const lastDateStr = format(lastDate, 'MMM d, yyyy', tzOption)

  const postsLeftSuffix =
    iterationsLeft !== undefined ? ` (${iterationsLeft} posts left)` : ''

  return `This post will be shared ${cadenceStr} at ${timeStr}, until ${lastDateStr}${postsLeftSuffix}.`
}

/**
 * Returns a human-readable recurrence summary for a post, or null if
 * recurrence is absent or the feature flag is disabled.
 */
export function getRecurrenceSummary(
  recurrence:
    | {
        cadence: { frequency: RecurrenceFrequency; frequencyStep: number }
        maxRecurrentPosts: number
        recurrentPostsLeft?: number
      }
    | null
    | undefined,
  dueAt: string | null | undefined,
  isEnabled: boolean,
  timeZone: string,
): string | null {
  if (!isEnabled || !recurrence) return null

  const { frequency, frequencyStep } = recurrence.cadence
  const startDate = dueAt ? new Date(dueAt) : null

  return startDate
    ? buildSummary(
        startDate,
        frequency,
        frequencyStep,
        recurrence.maxRecurrentPosts,
        timeZone,
        recurrence?.recurrentPostsLeft,
      )
    : 'This is a recurring post.'
}
